import React, { useId } from "react";
import { useBrand } from "@/app/components/theme/brand-theme-boundary";

export type TEfficiencyLabel = "A" | "B" | "C" | "D" | "E" | "F" | "G";

/**
 * Validates if a label is a valid efficiency label
 */
export const isValidEfficiencyLabel = (
  label: string | null
): label is TEfficiencyLabel => {
  return label !== null && ["A", "B", "C", "D", "E", "F", "G"].includes(label);
};

// Bar definitions: width (px) and color
const LABEL_BARS: Array<{
  label: TEfficiencyLabel;
  width: number;
  color: string;
}> = [
  { label: "A", width: 36, color: "#4AA359" },
  { label: "B", width: 48, color: "#6DB957" },
  { label: "C", width: 60, color: "#C3D652" },
  { label: "D", width: 72, color: "#fcd34d" },
  { label: "E", width: 84, color: "#F1B543" },
  { label: "F", width: 96, color: "#E37739" },
  { label: "G", width: 108, color: "#DA3832" },
];

// Height and style constants
const BAR_HEIGHT = 16;
const BAR_RADIUS = 3;
const BAR_GAP = 6;
const BLACK_BAR_WIDTH = 40;
const BLACK_BAR_COLOR = "#000";
const BLACK_TEXT_COLOR = "#fff";
const AUDI_POINTER_BAR_COLOR = "#657080";
const FONT_SIZE = 12; // 2px smaller than before
const FONT_FAMILY = "inherit";
const BLACK_BAR_HEIGHT = BAR_HEIGHT - 2; // black bar is 2px smaller in height

export type TEfficiencyLabelProps = {
  label: TEfficiencyLabel;
  secondLabel?: TEfficiencyLabel | null;
  className?: string;
};

/**
 * EfficiencyLabel & EfficiencyLabelShort components
 *
 * EfficiencyLabel: Renders a vertical stack of colored arrow bars (A-G) representing energy efficiency classes.
 * Highlights the selected class with a black pointer bar and label. Used for detailed efficiency visualization.
 * For hybrid vehicles, can display two labels side by side using the secondLabel prop.
 *
 * EfficiencyLabelShort: Renders a single colored arrow bar for a given label (A-G), compact for inline use.
 *
 * Props:
 * @prop {TEfficiencyLabel} label - The efficiency class to highlight ("A"-"G").
 * @prop {TEfficiencyLabel} [secondLabel] - Optional second label for hybrid vehicles, displayed side by side.
 * @prop {string} [className] - Optional additional class names for the SVG container.
 *
 * Example usage:
 * <EfficiencyLabel label="B" />
 * <EfficiencyLabel label="B" secondLabel="F" />
 * <EfficiencyLabelShort label="B" />
 */
export function EfficiencyLabel({
  label,
  secondLabel,
  className,
}: TEfficiencyLabelProps) {
  const { isAudi } = useBrand();
  const clipPathIdBase = useId().replace(/:/g, "");
  const barClipId = (barLabel: TEfficiencyLabel) =>
    `${clipPathIdBase}-bar-clip-${barLabel}`;
  const primaryPointerClipId = `${clipPathIdBase}-black-bar-clip-1`;
  const secondaryPointerClipId = `${clipPathIdBase}-black-bar-clip-2`;

  // Find the index of the selected labels
  const selectedIndex = LABEL_BARS.findIndex((bar) => bar.label === label);
  const secondSelectedIndex =
    secondLabel && isValidEfficiencyLabel(secondLabel)
      ? LABEL_BARS.findIndex((bar) => bar.label === secondLabel)
      : -1;
  // Calculate total height
  const totalHeight =
    LABEL_BARS.length * BAR_HEIGHT + (LABEL_BARS.length - 1) * BAR_GAP;
  // Horizontal gap between left and right block
  const HORIZONTAL_GAP = 32;
  // Gap between two black bars when displaying two labels
  const BLACK_BAR_GAP = 16;
  // SVG width: longest bar + gap + black bar(s)
  const svgWidth =
    LABEL_BARS[LABEL_BARS.length - 1].width +
    HORIZONTAL_GAP +
    BLACK_BAR_WIDTH +
    (secondSelectedIndex !== -1 ? BLACK_BAR_GAP + BLACK_BAR_WIDTH : 0);

  // Accessible label
  const ariaLabel =
    secondSelectedIndex !== -1
      ? `Energieeffizienzklasse: ${label} und ${secondLabel}`
      : `Energieeffizienzklasse: ${label}`;

  return (
    <svg
      width={svgWidth}
      height={totalHeight}
      viewBox={`0 0 ${svgWidth} ${totalHeight}`}
      className={className}
      aria-label={ariaLabel}
      role="img"
    >
      <title>
        {secondSelectedIndex !== -1
          ? `Energieeffizienzklasse: ${label} und ${secondLabel}`
          : `Energieeffizienzklasse: ${label}`}
      </title>
      {/* Render colored bars */}
      {LABEL_BARS.map((bar, i) => {
        const y = i * (BAR_HEIGHT + BAR_GAP);
        return (
          <g key={bar.label}>
            {/* Arrow-shaped colored bar */}
            <g>
              <clipPath id={barClipId(bar.label)}>
                <rect
                  x={0}
                  y={y}
                  width={bar.width}
                  height={BAR_HEIGHT}
                  rx={BAR_RADIUS}
                />
              </clipPath>
              <polygon
                points={`0,${y} ${bar.width - BAR_HEIGHT},${y} ${bar.width},${y + BAR_HEIGHT / 2} ${bar.width - BAR_HEIGHT},${y + BAR_HEIGHT} 0,${y + BAR_HEIGHT}`}
                fill={bar.color}
                clipPath={`url(#${barClipId(bar.label)})`}
              />
            </g>
            {/* Bar label text */}
            <text
              x={14}
              y={y + BAR_HEIGHT / 2}
              fontSize={FONT_SIZE}
              fontFamily={FONT_FAMILY}
              fontWeight="bold"
              fill="#fff"
              textAnchor="middle"
              dominantBaseline="middle"
              aria-hidden="true"
              dy=".1em"
              dx="-.4em"
            >
              {bar.label}
            </text>
          </g>
        );
      })}
      {/* Vertical divider line (only when two labels are displayed) */}
      {secondSelectedIndex !== -1 && (
        <line
          x1={
            LABEL_BARS[LABEL_BARS.length - 1].width +
            HORIZONTAL_GAP +
            BLACK_BAR_WIDTH +
            BLACK_BAR_GAP / 2
          }
          y1={0}
          x2={
            LABEL_BARS[LABEL_BARS.length - 1].width +
            HORIZONTAL_GAP +
            BLACK_BAR_WIDTH +
            BLACK_BAR_GAP / 2
          }
          y2={totalHeight}
          stroke="#e5e7eb"
          strokeWidth={1}
        />
      )}
      {/* Black pointer bar(s) */}
      {selectedIndex !== -1 && (
        <g>
          {/* First arrow-shaped black bar (points left) */}
          <g>
            <clipPath id={primaryPointerClipId}>
              <rect
                x={LABEL_BARS[LABEL_BARS.length - 1].width + HORIZONTAL_GAP}
                y={selectedIndex * (BAR_HEIGHT + BAR_GAP) + 1}
                width={BLACK_BAR_WIDTH}
                height={BLACK_BAR_HEIGHT}
                rx={BAR_RADIUS}
              />
            </clipPath>
            <polygon
              points={`
                ${LABEL_BARS[LABEL_BARS.length - 1].width + HORIZONTAL_GAP + BLACK_BAR_WIDTH},${selectedIndex * (BAR_HEIGHT + BAR_GAP) + 1}
                ${LABEL_BARS[LABEL_BARS.length - 1].width + HORIZONTAL_GAP + BAR_HEIGHT},${selectedIndex * (BAR_HEIGHT + BAR_GAP) + 1}
                ${LABEL_BARS[LABEL_BARS.length - 1].width + HORIZONTAL_GAP},${selectedIndex * (BAR_HEIGHT + BAR_GAP) + BLACK_BAR_HEIGHT / 2 + 1}
                ${LABEL_BARS[LABEL_BARS.length - 1].width + HORIZONTAL_GAP + BAR_HEIGHT},${selectedIndex * (BAR_HEIGHT + BAR_GAP) + BLACK_BAR_HEIGHT + 1}
                ${LABEL_BARS[LABEL_BARS.length - 1].width + HORIZONTAL_GAP + BLACK_BAR_WIDTH},${selectedIndex * (BAR_HEIGHT + BAR_GAP) + BLACK_BAR_HEIGHT + 1}
              `}
              fill={isAudi ? AUDI_POINTER_BAR_COLOR : BLACK_BAR_COLOR}
              clipPath={`url(#${primaryPointerClipId})`}
            />
          </g>
          {/* First label text, centered in black bar */}
          <text
            x={
              LABEL_BARS[LABEL_BARS.length - 1].width +
              HORIZONTAL_GAP +
              BLACK_BAR_WIDTH / 2
            }
            y={
              selectedIndex * (BAR_HEIGHT + BAR_GAP) + BLACK_BAR_HEIGHT / 2 + 1
            }
            fontSize={FONT_SIZE}
            fontFamily={FONT_FAMILY}
            fontWeight="bold"
            fill={BLACK_TEXT_COLOR}
            textAnchor="middle"
            dominantBaseline="middle"
            dy=".1em"
            dx=".85em"
          >
            {label}
          </text>
        </g>
      )}
      {/* Second black pointer bar (for hybrid vehicles) */}
      {secondSelectedIndex !== -1 && (
        <g>
          {/* Second arrow-shaped black bar (points left) */}
          <g>
            <clipPath id={secondaryPointerClipId}>
              <rect
                x={
                  LABEL_BARS[LABEL_BARS.length - 1].width +
                  HORIZONTAL_GAP +
                  BLACK_BAR_WIDTH +
                  BLACK_BAR_GAP
                }
                y={secondSelectedIndex * (BAR_HEIGHT + BAR_GAP) + 1}
                width={BLACK_BAR_WIDTH}
                height={BLACK_BAR_HEIGHT}
                rx={BAR_RADIUS}
              />
            </clipPath>
            <polygon
              points={`
                ${LABEL_BARS[LABEL_BARS.length - 1].width + HORIZONTAL_GAP + BLACK_BAR_WIDTH + BLACK_BAR_GAP + BLACK_BAR_WIDTH},${secondSelectedIndex * (BAR_HEIGHT + BAR_GAP) + 1}
                ${LABEL_BARS[LABEL_BARS.length - 1].width + HORIZONTAL_GAP + BLACK_BAR_WIDTH + BLACK_BAR_GAP + BAR_HEIGHT},${secondSelectedIndex * (BAR_HEIGHT + BAR_GAP) + 1}
                ${LABEL_BARS[LABEL_BARS.length - 1].width + HORIZONTAL_GAP + BLACK_BAR_WIDTH + BLACK_BAR_GAP},${secondSelectedIndex * (BAR_HEIGHT + BAR_GAP) + BLACK_BAR_HEIGHT / 2 + 1}
                ${LABEL_BARS[LABEL_BARS.length - 1].width + HORIZONTAL_GAP + BLACK_BAR_WIDTH + BLACK_BAR_GAP + BAR_HEIGHT},${secondSelectedIndex * (BAR_HEIGHT + BAR_GAP) + BLACK_BAR_HEIGHT + 1}
                ${LABEL_BARS[LABEL_BARS.length - 1].width + HORIZONTAL_GAP + BLACK_BAR_WIDTH + BLACK_BAR_GAP + BLACK_BAR_WIDTH},${secondSelectedIndex * (BAR_HEIGHT + BAR_GAP) + BLACK_BAR_HEIGHT + 1}
              `}
              fill={isAudi ? AUDI_POINTER_BAR_COLOR : BLACK_BAR_COLOR}
              clipPath={`url(#${secondaryPointerClipId})`}
            />
          </g>
          {/* Second label text, centered in black bar */}
          <text
            x={
              LABEL_BARS[LABEL_BARS.length - 1].width +
              HORIZONTAL_GAP +
              BLACK_BAR_WIDTH +
              BLACK_BAR_GAP +
              BLACK_BAR_WIDTH / 2
            }
            y={
              secondSelectedIndex * (BAR_HEIGHT + BAR_GAP) +
              BLACK_BAR_HEIGHT / 2 +
              1
            }
            fontSize={FONT_SIZE}
            fontFamily={FONT_FAMILY}
            fontWeight="bold"
            fill={BLACK_TEXT_COLOR}
            textAnchor="middle"
            dominantBaseline="middle"
            dy=".1em"
            dx=".85em"
          >
            {secondLabel}
          </text>
        </g>
      )}
    </svg>
  );
}

/**
 * EfficiencyLabelShort component renders a single short arrow bar for a given label (A-G).
 * The bar has a fixed length, correct color, and centered label. Accessibility included.
 */
export type TEfficiencyLabelShortProps = {
  label: TEfficiencyLabel;
  className?: string;
};

// Short bar constants for the small variant
const SHORT_BAR_WIDTH = 32; // reduced width
const SHORT_BAR_HEIGHT = 12; // reduced height
const SHORT_BAR_RADIUS = 2; // slightly smaller radius
const SHORT_FONT_SIZE = 10; // smaller font size

export function EfficiencyLabelShort({
  label,
  className,
}: TEfficiencyLabelShortProps) {
  const clipPathId = `${useId().replace(/:/g, "")}-short-bar-clip-${label}`;
  // Find color for the label
  const bar = LABEL_BARS.find((b) => b.label === label);
  const color = bar ? bar.color : "#ccc";
  // Accessible label
  const ariaLabel = `Energieeffizienzklasse: ${label}`;

  return (
    <svg
      width={SHORT_BAR_WIDTH}
      height={SHORT_BAR_HEIGHT}
      viewBox={`0 0 ${SHORT_BAR_WIDTH} ${SHORT_BAR_HEIGHT}`}
      className={className}
      aria-label={ariaLabel}
      role="img"
    >
      <title>{`Energieeffizienzklasse: ${label}`}</title>
      {/* Arrow-shaped colored bar */}
      <clipPath id={clipPathId}>
        <rect
          x={0}
          y={0}
          width={SHORT_BAR_WIDTH}
          height={SHORT_BAR_HEIGHT}
          rx={SHORT_BAR_RADIUS}
        />
      </clipPath>
      <polygon
        points={`0,0 ${SHORT_BAR_WIDTH - SHORT_BAR_HEIGHT},0 ${SHORT_BAR_WIDTH},${SHORT_BAR_HEIGHT / 2} ${SHORT_BAR_WIDTH - SHORT_BAR_HEIGHT},${SHORT_BAR_HEIGHT} 0,${SHORT_BAR_HEIGHT}`}
        fill={color}
        clipPath={`url(#${clipPathId})`}
      />
      {/* Centered label text */}
      <text
        x={11}
        y={SHORT_BAR_HEIGHT / 2}
        fontSize={SHORT_FONT_SIZE}
        fontFamily={FONT_FAMILY}
        fontWeight="bold"
        fill="#fff"
        textAnchor="middle"
        dominantBaseline="middle"
        aria-hidden="true"
        dy=".1em"
        dx="-.2em"
      >
        {label}
      </text>
    </svg>
  );
}
